// =====================================================
// Tarifario interno · tarifas, costes, conceptos y etiquetas
//
// Catálogo editable (tabla tariff_items + budget_tags) que usan Lizzi y la
// tabla «Presupuesto de la oferta» de cada licitación:
//   · tarifa = lo que se cobra por unidad · coste = lo que le cuesta a Impact Hub
//   · sin importe → en el presupuesto se estima el coste y la tarifa = coste × 1,05
// Cada cambio se guarda al momento y queda en el historial de la partida.
// =====================================================
import React from 'react';
import { Icon } from '../icons.jsx';
import { notify } from '../modals.jsx';
import {
  useBudgetCatalog, loadBudgetCatalog, updateTariffItem, createTariffItem, deleteTariffItem,
  fetchTariffHistory, updateBudgetTag, createBudgetTag, TARIFF_UNITS,
} from '../data/budget-catalog.js';
import { EXTERNAL_COST_FACTOR } from '../../supabase/functions/_shared/budget-catalog.js';
import {
  fmtEur, fmtPct, normalize, timeAgo, UNIT_SHORT, TagPill, TagPicker, NumField, TextField, ToneSwatches, usePopover, PopPanel,
} from './budget-ui.jsx';

const { useState, useMemo, useEffect } = React;
const FACTOR_TXT = String(EXTERNAL_COST_FACTOR).replace('.', ',');

const marginOf = (t) => (t.rate != null && t.cost != null && t.rate > 0 ? ((t.rate - t.cost) / t.rate) * 100 : null);
const userName = (id) => (window.MockData?.USERS || []).find((u) => u.id === id)?.name || (id ? 'Alguien del equipo' : 'Sistema');
const isAdmin = () => window.MockData?.CURRENT_USER?.role === 'admin';

function useSaver() {
  const [state, setState] = useState({}); // uuid → 'saving' | 'saved' | 'error'
  const run = async (key, fn) => {
    setState((s) => ({ ...s, [key]: 'saving' }));
    try {
      await fn();
      setState((s) => ({ ...s, [key]: 'saved' }));
      setTimeout(() => setState((s) => (s[key] === 'saved' ? { ...s, [key]: undefined } : s)), 1600);
      return true;
    } catch (e) {
      setState((s) => ({ ...s, [key]: 'error' }));
      notify('No se pudo guardar: ' + (e?.message || 'error'), 'AlertTriangle');
      return false;
    }
  };
  return [state, run];
}

function toCsv(items) {
  const head = ['Código', 'Grupo', 'Concepto', 'Unidad', 'Tarifa', 'Coste', 'Margen %', 'Etiqueta', 'Descripción', 'Activa'];
  const num = (n) => (n == null ? '' : String(Math.round(n * 10000) / 10000).replace('.', ','));
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = items.map((t) => [t.id, t.group, t.name, t.unit, num(t.rate), num(t.cost), num(marginOf(t)), t.tag, t.desc, t.active ? 'sí' : 'no'].map(esc).join(';'));
  return '﻿' + [head.map(esc).join(';'), ...lines].join('\n');
}

// ---------- Historial de una partida ----------
function HistoryButton({ item }) {
  const pop = usePopover(360, 320);
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!pop.open || !item.uuid) return;
    setRows(null);
    fetchTariffHistory(item.uuid).then(setRows).catch(() => setRows([]));
  }, [pop.open, item.uuid]);
  const label = { concept: 'Concepto', unit: 'Unidad', rate: 'Tarifa', cost: 'Coste', tag: 'Etiqueta', active: 'Estado' };
  const show = (k, v) => (v == null || v === '' ? '—' : k === 'rate' || k === 'cost' ? fmtEur(Number(v)) : k === 'active' ? (v ? 'activa' : 'archivada') : String(v));
  return (
    <>
      <button ref={pop.anchorRef} className="row-icon" title="Historial de cambios" onClick={() => pop.setOpen((o) => !o)} disabled={!item.uuid}>
        <Icon.Clock size={13}/>
      </button>
      <PopPanel pop={pop}>
          <div className="pp-title">Historial · <span className="code-badge">{item.id}</span></div>
          <div className="pp-list" style={{ maxHeight: 260 }}>
            {rows == null && <div className="pp-empty">Cargando…</div>}
            {rows?.length === 0 && <div className="pp-empty">Sin cambios desde la carga del tarifario 2027.</div>}
            {rows?.map((h) => {
              const keys = Object.keys(label).filter((k) => JSON.stringify(h.old_values?.[k]) !== JSON.stringify(h.new_values?.[k]));
              return (
                <div key={h.id} className="hist-row">
                  <div className="hist-meta">{userName(h.changed_by)} · {timeAgo(h.changed_at)}</div>
                  {keys.map((k) => (
                    <div key={k} className="hist-change">
                      <span className="hist-key">{label[k]}</span>
                      <span className="hist-old">{show(k, h.old_values?.[k])}</span>
                      <Icon.ArrowRight size={11}/>
                      <span className="hist-new">{show(k, h.new_values?.[k])}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
      </PopPanel>
    </>
  );
}

// ---------- Alta de partida ----------
function NewItemModal({ open, onClose, catalog, defaultGroup }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ code: '', name: '', group: defaultGroup || '', unit: 'ud', rate: null, cost: null, tag: '', desc: '' }); }, [open, defaultGroup]);
  if (!open) return null;
  const groups = [...new Set(catalog.items.map((t) => t.group))];
  const codeTaken = f.code && catalog.byId[f.code.trim()];
  const margin = marginOf({ rate: f.rate, cost: f.cost });
  const submit = async () => {
    if (!f.code?.trim() || !f.name?.trim()) { notify('Código y concepto son obligatorios', 'AlertTriangle'); return; }
    setBusy(true);
    try {
      await createTariffItem(f, window.MockData?.CURRENT_USER?.id);
      notify(`Partida ${f.code.trim()} añadida al tarifario`, 'CheckCircle');
      onClose();
    } catch (e) {
      notify('No se pudo crear: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally { setBusy(false); }
  };
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal tf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="tf-modal-head">
          <div>
            <div className="font-display" style={{ fontSize: 20 }}>Nueva partida</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 4 }}>Quedará disponible al momento en el selector «Concepto interno» de los presupuestos.</div>
          </div>
          <button className="row-icon" onClick={onClose} title="Cerrar"><Icon.X size={15}/></button>
        </div>
        <div className="tf-form">
          <label className="tf-field" style={{ gridColumn: 'span 1' }}>
            <span>Código</span>
            <input className={`tf-input mono${codeTaken ? ' is-invalid' : ''}`} value={f.code} placeholder="p. ej. P.7.1" autoFocus onChange={(e) => set('code')(e.target.value)}/>
            {codeTaken && <em className="tf-error">Ya existe</em>}
          </label>
          <label className="tf-field" style={{ gridColumn: 'span 3' }}>
            <span>Concepto</span>
            <input className="tf-input" value={f.name} placeholder="Nombre de la partida" onChange={(e) => set('name')(e.target.value)}/>
          </label>
          <label className="tf-field" style={{ gridColumn: 'span 2' }}>
            <span>Grupo</span>
            <input className="tf-input" list="tf-groups" value={f.group} placeholder="Personal, Catering…" onChange={(e) => set('group')(e.target.value)}/>
            <datalist id="tf-groups">{groups.map((g) => <option key={g} value={g}/>)}</datalist>
          </label>
          <label className="tf-field" style={{ gridColumn: 'span 2' }}>
            <span>Unidad</span>
            <div className="tf-units">
              {TARIFF_UNITS.map((u) => (
                <button key={u} type="button" className={f.unit === u ? 'is-active' : ''} onClick={() => set('unit')(u)}>{u}</button>
              ))}
            </div>
          </label>
          <div className="tf-field" style={{ gridColumn: 'span 1' }}>
            <span>Tarifa (€/{UNIT_SHORT[f.unit] || f.unit})</span>
            <NumField value={f.rate} ghost={false} onCommit={set('rate')} placeholder="Sin importe" align="left"/>
          </div>
          <div className="tf-field" style={{ gridColumn: 'span 1' }}>
            <span>Coste (€/{UNIT_SHORT[f.unit] || f.unit})</span>
            <NumField value={f.cost} ghost={false} onCommit={set('cost')} placeholder="Sin importe" align="left"/>
          </div>
          <div className="tf-field" style={{ gridColumn: 'span 2' }}>
            <span>Etiqueta de gasto</span>
            <div className="tf-tagbox"><TagPicker value={f.tag} tags={catalog.tags} tagByName={catalog.tagByName} onChange={set('tag')}/></div>
          </div>
          <label className="tf-field" style={{ gridColumn: 'span 4' }}>
            <span>Descripción <em>(opcional · qué incluye)</em></span>
            <textarea className="tf-input" rows={2} value={f.desc} onChange={(e) => set('desc')(e.target.value)}/>
          </label>
        </div>
        <div className="tf-modal-foot">
          <div style={{ fontSize: 12, color: 'var(--text-3)', flex: '1 1 240px' }}>
            {margin != null ? <>Margen sobre tarifa: <strong style={{ color: margin < 0 ? 'var(--sem-danger)' : 'var(--text-1)' }}>{fmtPct(margin)}</strong></>
              : `Sin tarifa o coste: en el presupuesto se estimará (tarifa = coste × ${FACTOR_TXT}).`}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary" onClick={submit} disabled={busy || codeTaken}><Icon.Plus size={14}/> Añadir partida</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Etiquetas ----------
function TagsPanel({ catalog, saving, run }) {
  const [adding, setAdding] = useState(null);
  const counts = useMemo(() => {
    const c = {};
    catalog.active.forEach((t) => { c[t.tag || ''] = (c[t.tag || ''] || 0) + 1; });
    return c;
  }, [catalog]);
  const add = async () => {
    const ok = await run('tag:new', () => createBudgetTag(adding));
    if (ok) { notify(`Etiqueta «${adding.name}» creada`, 'CheckCircle'); setAdding(null); }
  };
  return (
    <div className="card tf-tags">
      {catalog.tags.map((t) => (
        <div key={t.name} className="tf-tag-row">
          <div className="tf-tag-name">
            <TagPill tag={t.name} tone={t.tone}/>
            <span className="tf-tag-count">{counts[t.name] || 0} partidas</span>
          </div>
          <TextField value={t.desc} multiline rows={3} placeholder="Qué gastos entran en esta etiqueta" disabled={catalog.source !== 'db'}
            onCommit={(v) => run(`tag:${t.name}`, () => updateBudgetTag(t.name, { desc: v }))}/>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ToneSwatches value={t.tone} onChange={(tone) => run(`tag:${t.name}`, () => updateBudgetTag(t.name, { tone }))}/>
            <SaveMark state={saving[`tag:${t.name}`]}/>
          </div>
        </div>
      ))}
      {adding ? (
        <div className="tf-tag-row is-new">
          <input className="tf-input" autoFocus placeholder="Nombre de la etiqueta" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })}/>
          <input className="tf-input" placeholder="Definición (la usa Lizzi para clasificar)" value={adding.desc} onChange={(e) => setAdding({ ...adding, desc: e.target.value })}/>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ToneSwatches value={adding.tone} onChange={(tone) => setAdding({ ...adding, tone })}/>
            <button className="btn btn-primary" onClick={add} disabled={!adding.name.trim()}>Crear</button>
            <button className="btn btn-ghost" onClick={() => setAdding(null)}>Cancelar</button>
          </div>
        </div>
      ) : (
        <button className="tf-add-row" onClick={() => setAdding({ name: '', desc: '', tone: 'violet' })} disabled={catalog.source !== 'db'}>
          <Icon.Plus size={13}/> Nueva etiqueta
        </button>
      )}
    </div>
  );
}

function SaveMark({ state }) {
  if (state === 'saving') return <span className="save-mark is-busy" title="Guardando"/>;
  if (state === 'saved') return <span className="save-mark is-ok" title="Guardado"><Icon.Check size={11}/></span>;
  if (state === 'error') return <span className="save-mark is-error" title="No se pudo guardar"><Icon.AlertCircle size={11}/></span>;
  return null;
}

// ---------- Pantalla ----------
function TariffScreen() {
  const catalog = useBudgetCatalog();
  const [view, setView] = useState('items');
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [openDesc, setOpenDesc] = useState({});
  const [newOpen, setNewOpen] = useState(false);
  const [saving, run] = useSaver();
  const editable = catalog.source === 'db';

  const base = showArchived ? catalog.items : catalog.active;
  const groups = useMemo(() => {
    const m = new Map();
    base.forEach((t) => m.set(t.group, (m.get(t.group) || 0) + 1));
    return [...m.entries()];
  }, [base]);
  const nq = normalize(q);
  const list = base.filter((t) => {
    if (group !== 'all' && t.group !== group) return false;
    if (tagFilter && t.tag !== tagFilter) return false;
    if (onlyMissing && t.rate != null && t.cost != null) return false;
    if (nq && !normalize(`${t.id} ${t.name} ${t.group} ${t.tag} ${t.desc}`).includes(nq)) return false;
    return true;
  });
  const grouped = useMemo(() => {
    const m = new Map();
    list.forEach((t) => { if (!m.has(t.group)) m.set(t.group, []); m.get(t.group).push(t); });
    return [...m.entries()];
  }, [list]);

  const priced = catalog.active.filter((t) => t.rate != null && t.cost != null);
  const missing = catalog.active.length - priced.length;
  const margins = priced.map(marginOf).filter((m) => m != null).sort((a, b) => a - b);
  const medianMargin = margins.length ? margins[Math.floor(margins.length / 2)] : null;
  // Solo cuentan las ediciones del equipo (la carga inicial no tiene autor).
  const lastEdit = catalog.items.reduce((acc, t) => (t.updatedBy && t.updatedAt && (!acc || t.updatedAt > acc.updatedAt) ? t : acc), null);

  const save = (t, patch) => run(t.uuid, () => updateTariffItem(t.uuid, patch));
  const archive = async (t) => {
    const ok = await save(t, { active: !t.active });
    if (ok) notify(t.active ? `${t.id} archivada: ya no se ofrecerá en presupuestos nuevos` : `${t.id} reactivada`, t.active ? 'Folder' : 'Refresh');
  };
  const remove = async (t) => {
    if (!window.confirm(`¿Eliminar definitivamente ${t.id} · ${t.name}? Los presupuestos que ya la usan conservan sus importes.`)) return;
    try { await deleteTariffItem(t.uuid); notify(`${t.id} eliminada`, 'Trash'); } catch (e) { notify(e?.message || 'No se pudo eliminar', 'AlertTriangle'); }
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(catalog.items)], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `tarifario-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const clearFilters = () => { setQ(''); setGroup('all'); setTagFilter(''); setOnlyMissing(false); };
  const filtersOn = q || group !== 'all' || tagFilter || onlyMissing;

  return (
    <div className="viewport-inner fade-in tf">
      <div className="page-head" style={{ marginBottom: 24 }}>
        <div>
          <div className="page-eyebrow">Catálogo · {catalog.active.length} partidas · {catalog.tags.length} etiquetas</div>
          <h1 className="page-title">Tarifario interno</h1>
          <p className="muted" style={{ fontSize: 14, marginTop: 2, maxWidth: 680, lineHeight: 1.55 }}>
            Tarifas (lo que se cobra) y costes (lo que le cuesta a Impact Hub) que Lizzi aplica en el <strong>Presupuesto de la oferta</strong> de cada licitación.
            Los cambios se guardan al momento; los presupuestos ya hechos te avisarán para actualizarse.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline" onClick={exportCsv}><Icon.Download size={14}/> Exportar CSV</button>
          {view === 'items' && <button className="btn btn-primary" onClick={() => setNewOpen(true)} disabled={!editable}><Icon.Plus size={14}/> Nueva partida</button>}
        </div>
      </div>

      {!catalog.loaded && <div className="callout callout-info" style={{ marginBottom: 16 }}><div className="callout-icon"><Icon.Clock size={16}/></div><div>Cargando el tarifario…</div></div>}
      {catalog.loaded && !editable && (
        <div className="callout callout-warn" style={{ marginBottom: 16 }}>
          <div className="callout-icon"><Icon.AlertTriangle size={16}/></div>
          <div style={{ flex: 1 }}>Mostrando el tarifario 2027 de referencia: no se ha podido leer el tarifario editable de la base de datos.</div>
          <button className="btn btn-outline" onClick={() => loadBudgetCatalog({ force: true })}><Icon.Refresh size={14}/> Reintentar</button>
        </div>
      )}

      <div className="tf-kpis">
        <div className="tf-kpi">
          <div className="tf-kpi-label">Partidas activas</div>
          <div className="tf-kpi-value">{catalog.active.length}</div>
          <div className="tf-kpi-sub">{groups.length} grupos{catalog.items.length > catalog.active.length ? ` · ${catalog.items.length - catalog.active.length} archivadas` : ''}</div>
        </div>
        <button type="button" className={`tf-kpi is-click${onlyMissing ? ' is-active' : ''}`} onClick={() => setOnlyMissing((v) => !v)} disabled={!missing}>
          <div className="tf-kpi-label">Sin importe</div>
          <div className="tf-kpi-value" style={{ color: missing ? 'var(--sem-warning)' : undefined }}>{missing}</div>
          <div className="tf-kpi-sub">{missing ? 'Se estiman en cada presupuesto · ver' : 'Todas tienen tarifa y coste'}</div>
        </button>
        <div className="tf-kpi">
          <div className="tf-kpi-label">Margen mediano</div>
          <div className="tf-kpi-value">{fmtPct(medianMargin, 0)}</div>
          <div className="tf-kpi-sub">(tarifa − coste) / tarifa · sin tarifa: coste × {FACTOR_TXT}</div>
        </div>
        <div className="tf-kpi">
          <div className="tf-kpi-label">Última modificación</div>
          <div className="tf-kpi-value is-text">{lastEdit ? timeAgo(lastEdit.updatedAt) : 'Versión inicial'}</div>
          <div className="tf-kpi-sub">{lastEdit ? <><span className="code-badge">{lastEdit.id}</span> · {userName(lastEdit.updatedBy)}</> : 'Tarifario 2027 · sin cambios desde la carga'}</div>
        </div>
      </div>

      <div className="tf-toolbar">
        <div className="bb-seg" role="tablist">
          <button role="tab" aria-selected={view === 'items'} className={view === 'items' ? 'is-active' : ''} onClick={() => setView('items')}>
            Partidas<span className="bb-seg-count">{catalog.active.length}</span>
          </button>
          <button role="tab" aria-selected={view === 'tags'} className={view === 'tags' ? 'is-active' : ''} onClick={() => setView('tags')}>
            Etiquetas<span className="bb-seg-count">{catalog.tags.length}</span>
          </button>
        </div>
        {view === 'items' && (
          <div className="tf-filters">
            <div className="bb-search">
              <Icon.Search size={13}/>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar código, concepto…"/>
              {q && <button className="row-icon" onClick={() => setQ('')}><Icon.X size={11}/></button>}
            </div>
            <div className="tf-tagfilter">
              <TagPicker value={tagFilter} tags={catalog.tags} tagByName={catalog.tagByName} onChange={setTagFilter} compact emptyLabel="Todas las etiquetas"/>
            </div>
            <label className="tf-check"><input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}/> Archivadas</label>
            {filtersOn && <button className="bb-link" onClick={clearFilters}>Limpiar filtros</button>}
          </div>
        )}
      </div>

      {view === 'tags' ? <TagsPanel catalog={catalog} saving={saving} run={run}/> : (
        <>
          <div className="tf-groups">
            <button className={`tf-group-chip${group === 'all' ? ' is-active' : ''}`} onClick={() => setGroup('all')}>Todos <span>{base.length}</span></button>
            {groups.map(([g, n]) => (
              <button key={g} className={`tf-group-chip${group === g ? ' is-active' : ''}`} onClick={() => setGroup(group === g ? 'all' : g)}>{g} <span>{n}</span></button>
            ))}
          </div>

          <div className="card tf-table-wrap">
            <table className="tf-table">
              <thead>
                <tr>
                  <th style={{ width: 96 }}>Código</th>
                  <th>Concepto</th>
                  <th style={{ width: 92 }}>Unidad</th>
                  <th className="num" style={{ width: 132 }}>Tarifa</th>
                  <th className="num is-cost" style={{ width: 132 }}>Coste</th>
                  <th className="num" style={{ width: 92 }} title="(tarifa − coste) / tarifa">Margen</th>
                  <th style={{ width: 250 }}>Etiqueta</th>
                  <th style={{ width: 104 }}></th>
                </tr>
              </thead>
              <tbody>
                {grouped.map(([g, items]) => {
                  const isCol = !!collapsed[g];
                  return (
                    <React.Fragment key={g}>
                      <tr className="tf-group-row">
                        <td colSpan={8}>
                          <button className="tf-group-toggle" onClick={() => setCollapsed((c) => ({ ...c, [g]: !isCol }))}>
                            <Icon.ChevronDown size={13} style={{ transform: isCol ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms ease' }}/>
                            {g}<span>{items.length}</span>
                          </button>
                        </td>
                      </tr>
                      {!isCol && items.map((t) => {
                        const m = marginOf(t);
                        const missingAmt = t.rate == null || t.cost == null;
                        const descOpen = !!openDesc[t.id];
                        return (
                          <tr key={t.id} className={`tf-row${t.active ? '' : ' is-archived'}`}>
                            <td><span className="code-badge">{t.id}</span></td>
                            <td>
                              <TextField value={t.name} strong disabled={!editable} ariaLabel="Concepto" onCommit={(v) => v && save(t, { name: v })}/>
                              {(t.desc || descOpen) && (
                                descOpen
                                  ? <div className="tf-desc-edit"><TextField value={t.desc} multiline disabled={!editable} placeholder="Qué incluye esta partida" onCommit={(v) => save(t, { desc: v })}/></div>
                                  : <button className="tf-desc" onClick={() => setOpenDesc((o) => ({ ...o, [t.id]: true }))} title="Editar descripción">{t.desc}</button>
                              )}
                            </td>
                            <td>
                              <select className="tf-unit" value={t.unit} disabled={!editable} onChange={(e) => save(t, { unit: e.target.value })}>
                                {[...new Set([...TARIFF_UNITS, t.unit])].map((u) => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                            <td className="num">
                              <NumField value={t.rate} suffix="€" placeholder="—" disabled={!editable} invalid={missingAmt && t.rate == null} decimals={4} ariaLabel="Tarifa"
                                onCommit={(v) => save(t, { rate: v })}/>
                            </td>
                            <td className="num is-cost">
                              <NumField value={t.cost} suffix="€" placeholder="—" disabled={!editable} invalid={missingAmt && t.cost == null} decimals={4} ariaLabel="Coste"
                                onCommit={(v) => save(t, { cost: v })}/>
                            </td>
                            <td className="num">
                              {missingAmt
                                ? <span className="tf-missing" title={`Sin importe: en el presupuesto se estima el coste y la tarifa = coste × ${FACTOR_TXT}`}>Estimar</span>
                                : <span className={`tf-margin${m < 0 ? ' is-neg' : ''}`}>{fmtPct(m)}</span>}
                            </td>
                            <td><TagPicker value={t.tag} tags={catalog.tags} tagByName={catalog.tagByName} disabled={!editable} onChange={(v) => save(t, { tag: v })}/></td>
                            <td>
                              <div className="row-actions">
                                <SaveMark state={saving[t.uuid]}/>
                                {!t.desc && !descOpen && editable && (
                                  <button className="row-icon" title="Añadir descripción" onClick={() => setOpenDesc((o) => ({ ...o, [t.id]: true }))}><Icon.Edit size={12}/></button>
                                )}
                                <HistoryButton item={t}/>
                                <button className="row-icon" title={t.active ? 'Archivar (no se ofrecerá en presupuestos nuevos)' : 'Reactivar'} disabled={!editable} onClick={() => archive(t)}>
                                  {t.active ? <Icon.Folder size={13}/> : <Icon.Refresh size={13}/>}
                                </button>
                                {isAdmin() && !t.active && (
                                  <button className="row-icon is-danger" title="Eliminar definitivamente" onClick={() => remove(t)}><Icon.Trash size={12}/></button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
                {!list.length && (
                  <tr><td colSpan={8} className="tf-empty">
                    Ninguna partida coincide. {filtersOn && <button className="bb-link" onClick={clearFilters}>Limpiar filtros</button>}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="tf-legend">
            <span><TagPill tag="Etiqueta" tone="violet" size="sm"/> clasifica el gasto en el presupuesto</span>
            <span><span className="tf-missing">Estimar</span> sin importe: Lizzi estima el coste y la tarifa es coste × {FACTOR_TXT}</span>
            <span><Icon.Folder size={12}/> archivar la oculta en presupuestos nuevos sin romper los existentes</span>
          </div>
        </>
      )}

      <NewItemModal open={newOpen} onClose={() => setNewOpen(false)} catalog={catalog} defaultGroup={group !== 'all' ? group : ''}/>
    </div>
  );
}

window.TariffScreen = TariffScreen;

export { TariffScreen };
